import { useState, useEffect } from "react";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  BookA,
  Plus,
  Trash2,
  RefreshCw,
  Check,
  X,
  AlertTriangle,
  Loader2,
  Code2,
  FileCode,
  GitPullRequest,
  ExternalLink,
} from "lucide-react";
import type { GlossaryEntry, CodeLocation } from "@iamu/shared";

interface Props {
  projectId: string;
}

export function ProjectTerminologyTab({ projectId }: Props) {
  const {
    data: glossary,
    isLoading: glossaryLoading,
    error: glossaryError,
    refetch: refetchGlossary,
  } = trpc.terminology.getGlossary.useQuery({ projectId });
  const {
    data: analysisStatus,
    isLoading: statusLoading,
    error: statusError,
    refetch: refetchStatus,
  } = trpc.terminology.getAnalysisStatus.useQuery({ projectId });
  const { data: analysisResults } =
    trpc.terminology.getAnalysisResults.useQuery({ projectId });

  const utils = trpc.useUtils();

  const isLoading = glossaryLoading || statusLoading;
  const error = glossaryError || statusError;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24" />
        <Skeleton className="h-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
          <AlertTriangle className="h-10 w-10 text-destructive" />
          <div className="space-y-2">
            <h2 className="text-lg font-semibold">
              Failed to load terminology data
            </h2>
            <p className="max-w-md text-sm text-muted-foreground">
              We couldn't retrieve the terminology data for this project. Please
              try again.
            </p>
          </div>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => {
              refetchGlossary();
              refetchStatus();
            }}
          >
            <RefreshCw className="h-4 w-4" />
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <AnalysisStatusSection
        projectId={projectId}
        status={analysisStatus}
        utils={utils}
      />

      {analysisResults?.results && (
        <InconsistenciesSection
          projectId={projectId}
          analysisId={analysisResults.id}
          results={analysisResults.results}
          utils={utils}
        />
      )}

      {analysisResults?.results && (
        <ExtractedTermsSection
          projectId={projectId}
          extractedTerms={analysisResults.results.extractedTerms}
          glossary={glossary ?? []}
          utils={utils}
        />
      )}

      <GlossarySection
        projectId={projectId}
        glossary={glossary ?? []}
        utils={utils}
      />

      <CodeReviewSection projectId={projectId} />
    </div>
  );
}

// ── Analysis Status Section ────────────────────────────────────────

interface AnalysisStatusProps {
  projectId: string;
  status:
    | {
        status: "never_analyzed" | "running" | "stale" | "up_to_date";
        analysis: unknown;
        staleness: { newWalkthroughs: number; newSources: number } | null;
      }
    | undefined;
  utils: ReturnType<typeof trpc.useUtils>;
}

function AnalysisStatusSection({
  projectId,
  status,
  utils,
}: AnalysisStatusProps) {
  const triggerMutation = trpc.terminology.triggerAnalysis.useMutation({
    onSuccess: () => {
      toast.success("Analysis started");
      void utils.terminology.getAnalysisStatus.invalidate({ projectId });
    },
    onError: (err) => toastTrpcError(err, "Failed to start analysis"),
  });

  if (!status || status.status === "never_analyzed") {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
          <BookA className="h-10 w-10 text-muted-foreground" />
          <div className="space-y-2">
            <h2 className="text-lg font-semibold">
              No terminology analysis yet
            </h2>
            <p className="max-w-md text-sm text-muted-foreground">
              Run an analysis to detect terminology inconsistencies across your
              walkthroughs, scenarios, and sources. Resolved inconsistencies
              build your project glossary.
            </p>
          </div>
          <Button
            onClick={() => triggerMutation.mutate({ projectId })}
            disabled={triggerMutation.isPending}
          >
            {triggerMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Run first analysis
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (status.status === "running") {
    return (
      <Card>
        <CardContent className="flex items-center gap-3 py-4">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <div>
            <p className="text-sm font-medium">Analysis running</p>
            <p className="text-xs text-muted-foreground">
              Extracting terms and detecting inconsistencies...
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (status.status === "stale" && status.staleness) {
    const parts: string[] = [];
    if (status.staleness.newWalkthroughs > 0) {
      parts.push(
        `${status.staleness.newWalkthroughs} new walkthrough${status.staleness.newWalkthroughs > 1 ? "s" : ""}`,
      );
    }
    if (status.staleness.newSources > 0) {
      parts.push(
        `${status.staleness.newSources} new source${status.staleness.newSources > 1 ? "s" : ""}`,
      );
    }

    return (
      <Card>
        <CardContent className="flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-severity-important" />
            <div>
              <p className="text-sm font-medium">Analysis may be stale</p>
              <p className="text-xs text-muted-foreground">
                {parts.join(" and ")} since last analysis
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => triggerMutation.mutate({ projectId })}
            disabled={triggerMutation.isPending}
          >
            {triggerMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Re-analyze
          </Button>
        </CardContent>
      </Card>
    );
  }

  // up_to_date
  return (
    <Card>
      <CardContent className="flex items-center justify-between py-4">
        <div className="flex items-center gap-3">
          <Check className="h-5 w-5 text-finding-helps" />
          <div>
            <p className="text-sm font-medium">Analysis up to date</p>
            <p className="text-xs text-muted-foreground">
              All content has been analyzed
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => triggerMutation.mutate({ projectId })}
          disabled={triggerMutation.isPending}
        >
          {triggerMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Re-analyze
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Inconsistencies Section ────────────────────────────────────────

interface InconsistenciesSectionProps {
  projectId: string;
  analysisId: string;
  results: {
    extractedTerms: Array<{ term: string; source: string; context: string }>;
    inconsistencies: Array<{
      id: string;
      variants: Array<{
        term: string;
        occurrences: Array<{ source: string; context: string }>;
      }>;
      dismissed: boolean;
      resolvedEntryId?: string;
    }>;
  };
  utils: ReturnType<typeof trpc.useUtils>;
}

function InconsistenciesSection({
  projectId,
  analysisId,
  results,
  utils,
}: InconsistenciesSectionProps) {
  const unresolved = results.inconsistencies.filter(
    (inc) => !inc.dismissed && !inc.resolvedEntryId,
  );

  const resolveMutation = trpc.terminology.resolveInconsistency.useMutation({
    onSuccess: () => {
      toast.success("Inconsistency resolved");
      void utils.terminology.getGlossary.invalidate({ projectId });
      void utils.terminology.getAnalysisResults.invalidate({ projectId });
    },
    onError: (err) => toastTrpcError(err, "Failed to resolve inconsistency"),
  });

  const dismissMutation = trpc.terminology.dismissInconsistency.useMutation({
    onSuccess: () => {
      toast.success("Inconsistency dismissed");
      void utils.terminology.getAnalysisResults.invalidate({ projectId });
    },
    onError: (err) => toastTrpcError(err, "Failed to dismiss inconsistency"),
  });

  if (unresolved.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="h-4 w-4" />
          Unresolved inconsistencies
          <Badge variant="secondary">{unresolved.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {unresolved.map((inc) => (
          <InconsistencyCard
            key={inc.id}
            inconsistency={inc}
            isPending={resolveMutation.isPending || dismissMutation.isPending}
            onResolve={(canonicalTerm, variants) => {
              const entry: GlossaryEntry = {
                id: crypto.randomUUID(),
                canonicalTerm,
                variants: variants.filter((v) => v !== canonicalTerm),
                sourceRefs: [],
                addedBy: "analysis",
                addedAt: new Date().toISOString(),
              };
              resolveMutation.mutate({
                projectId,
                analysisId,
                inconsistencyId: inc.id,
                entry,
              });
            }}
            onDismiss={() => {
              dismissMutation.mutate({
                projectId,
                analysisId,
                inconsistencyId: inc.id,
              });
            }}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function InconsistencyCard({
  inconsistency,
  isPending,
  onResolve,
  onDismiss,
}: {
  inconsistency: InconsistenciesSectionProps["results"]["inconsistencies"][0];
  isPending: boolean;
  onResolve: (canonical: string, variants: string[]) => void;
  onDismiss: () => void;
}) {
  const allTerms = inconsistency.variants.map((v) => v.term);

  return (
    <div className="rounded-lg border p-4 space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {inconsistency.variants.map((v) => (
              <Badge key={v.term} variant="outline">
                {v.term}
                <span className="ml-1 text-muted-foreground">
                  ({v.occurrences.length})
                </span>
              </Badge>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Found in:{" "}
            {inconsistency.variants
              .flatMap((v) => v.occurrences.map((o) => o.source))
              .filter((v, i, a) => a.indexOf(v) === i)
              .join(", ")}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          disabled={isPending}
          aria-label="Dismiss inconsistency"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        <span className="text-xs text-muted-foreground self-center">
          Pick canonical:
        </span>
        {allTerms.map((term) => (
          <Button
            key={term}
            variant="outline"
            size="sm"
            onClick={() => onResolve(term, allTerms)}
            disabled={isPending}
          >
            <Check className="mr-1 h-3 w-3" />
            {term}
          </Button>
        ))}
      </div>
    </div>
  );
}

// ── Extracted Terms Section ────────────────────────────────────────

interface ExtractedTermsSectionProps {
  projectId: string;
  extractedTerms: Array<{ term: string; source: string; context: string }>;
  glossary: GlossaryEntry[];
  utils: ReturnType<typeof trpc.useUtils>;
}

function ExtractedTermsSection({
  projectId,
  extractedTerms,
  glossary,
  utils,
}: ExtractedTermsSectionProps) {
  const updateMutation = trpc.terminology.updateGlossary.useMutation({
    onSuccess: (updated) => {
      utils.terminology.getGlossary.setData({ projectId }, updated);
      toast.success("Term added to glossary");
    },
    onError: (err) => toastTrpcError(err, "Failed to add term to glossary"),
  });

  // Filter out terms already in the glossary (by canonical term or variant)
  const glossaryTerms = new Set(
    glossary.flatMap((e) => [
      e.canonicalTerm.toLowerCase(),
      ...e.variants.map((v) => v.toLowerCase()),
    ]),
  );

  const promotable = extractedTerms.filter(
    (t) => !glossaryTerms.has(t.term.toLowerCase().trim()),
  );

  if (promotable.length === 0) return null;

  function promoteToGlossary(term: { term: string; context: string }) {
    const newEntry: GlossaryEntry = {
      id: crypto.randomUUID(),
      canonicalTerm: term.term,
      definition: term.context,
      variants: [],
      sourceRefs: [],
      addedBy: "analysis",
      addedAt: new Date().toISOString(),
    };
    updateMutation.mutate({
      projectId,
      glossary: [...glossary, newEntry],
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <BookA className="h-4 w-4" />
          Extracted terms
          <Badge variant="secondary">{promotable.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-4 text-sm text-muted-foreground">
          Terms discovered by the analysis. Add the ones you want to track in
          your glossary.
        </p>
        <div className="space-y-2">
          {promotable.map((term) => (
            <div
              key={`${term.term}-${term.source}`}
              className="flex items-start justify-between gap-4 rounded-lg border p-3"
            >
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-medium">{term.term}</p>
                <p className="text-sm text-muted-foreground truncate">
                  {term.context}
                </p>
                <Badge variant="outline" className="text-xs">
                  {term.source}
                </Badge>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => promoteToGlossary(term)}
                disabled={updateMutation.isPending}
                aria-label={`Add "${term.term}" to glossary`}
              >
                {updateMutation.isPending ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <Plus className="mr-1 h-3 w-3" />
                )}
                Add
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Code Review Section ────────────────────────────────────────────

function CodeReviewSection({ projectId }: { projectId: string }) {
  const [reviewResult, setReviewResult] = useState<{
    walkthroughId: string;
    filesReviewed: number;
    findingsCount: number;
  } | null>(null);

  const reviewMutation = trpc.walkthrough.runTerminologyReview.useMutation({
    onSuccess: (data) => {
      setReviewResult(data);
      toast.success(
        `Code review complete: ${data.findingsCount} issue${data.findingsCount !== 1 ? "s" : ""} found in ${data.filesReviewed} files`,
      );
    },
    onError: (err) => toastTrpcError(err, "Failed to run code review"),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          <Code2 className="h-4 w-4" />
          Code review
        </CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={() => reviewMutation.mutate({ projectId })}
          disabled={reviewMutation.isPending}
        >
          {reviewMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Code2 className="mr-2 h-4 w-4" />
          )}
          {reviewMutation.isPending ? "Reviewing..." : "Review Code"}
        </Button>
      </CardHeader>
      <CardContent>
        {reviewResult ? (
          <CodeReviewResults
            projectId={projectId}
            walkthroughId={reviewResult.walkthroughId}
            filesReviewed={reviewResult.filesReviewed}
            findingsCount={reviewResult.findingsCount}
          />
        ) : (
          <p className="py-4 text-center text-sm text-muted-foreground">
            Review your codebase for terminology consistency against the
            glossary. Requires a connected GitHub repository and at least one
            glossary entry.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function CodeReviewResults({
  projectId: _projectId,
  walkthroughId,
  filesReviewed,
  findingsCount,
}: {
  projectId: string;
  walkthroughId: string;
  filesReviewed: number;
  findingsCount: number;
}) {
  const { data: walkthrough } = trpc.walkthrough.getById.useQuery({
    id: walkthroughId,
  });

  const applyFix = trpc.finding.applyTerminologyFix.useMutation({
    onSuccess: (data) => {
      toast.success("Fix PR created", {
        action: {
          label: "Open PR",
          onClick: () => window.open(data.prUrl, "_blank"),
        },
      });
    },
    onError: (err) => toastTrpcError(err, "Failed to apply terminology fix"),
  });

  if (!walkthrough) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          Reviewed {filesReviewed} files, found {findingsCount} issue
          {findingsCount !== 1 ? "s" : ""}.
        </p>
        <Skeleton className="h-24" />
      </div>
    );
  }

  const codeFindings = walkthrough.findings.filter(
    (f) => f.codeLocation != null,
  );

  if (codeFindings.length === 0) {
    return (
      <div className="flex items-center gap-2 py-4">
        <Check className="h-5 w-5 text-finding-helps" />
        <p className="text-sm">
          No terminology mismatches found in {filesReviewed} files.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Found {codeFindings.length} terminology issue
        {codeFindings.length !== 1 ? "s" : ""} in {filesReviewed} files.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>File</TableHead>
            <TableHead>Identifier</TableHead>
            <TableHead>Suggested rename</TableHead>
            <TableHead>Confidence</TableHead>
            <TableHead className="w-[100px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {codeFindings.map((finding) => {
            const loc = finding.codeLocation as CodeLocation;
            return (
              <TableRow key={finding.id}>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <FileCode className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="font-mono text-xs">
                      {loc.filePath}
                      {loc.line ? `:${loc.line}` : ""}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                    {loc.identifier}
                  </code>
                </TableCell>
                <TableCell>
                  <code className="rounded bg-mark-helps/10 px-1.5 py-0.5 text-xs text-finding-helps">
                    {loc.suggestedRename}
                  </code>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={
                      finding.confidence === "high"
                        ? "default"
                        : finding.confidence === "medium"
                          ? "secondary"
                          : "outline"
                    }
                  >
                    {finding.confidence ?? "unknown"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => applyFix.mutate({ findingId: finding.id })}
                    disabled={
                      applyFix.isPending || finding.fixStatus !== "open"
                    }
                    aria-label={`Create fix PR for ${loc.identifier}`}
                  >
                    {applyFix.isPending ? (
                      <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    ) : finding.fixStatus !== "open" ? (
                      <ExternalLink className="mr-1 h-3 w-3" />
                    ) : (
                      <GitPullRequest className="mr-1 h-3 w-3" />
                    )}
                    {finding.fixStatus !== "open" ? "PR Created" : "Fix PR"}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// ── Glossary Section ────────────────────────────────────────────

interface GlossarySectionProps {
  projectId: string;
  glossary: GlossaryEntry[];
  utils: ReturnType<typeof trpc.useUtils>;
}

function GlossarySection({
  projectId,
  glossary: serverGlossary,
  utils,
}: GlossarySectionProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [localEntries, setLocalEntries] =
    useState<GlossaryEntry[]>(serverGlossary);

  // Sync from server when not editing
  useEffect(() => {
    if (!editingId) {
      setLocalEntries(serverGlossary);
    }
  }, [serverGlossary, editingId]);

  const updateMutation = trpc.terminology.updateGlossary.useMutation({
    onSuccess: (updated) => {
      utils.terminology.getGlossary.setData({ projectId }, updated);
      setLocalEntries(updated);
      toast.success("Glossary updated");
      setEditingId(null);
    },
    onError: (err) => toastTrpcError(err, "Failed to update glossary"),
  });

  function addEntry() {
    const newEntry: GlossaryEntry = {
      id: crypto.randomUUID(),
      canonicalTerm: "",
      variants: [],
      sourceRefs: [],
      addedBy: "user",
      addedAt: new Date().toISOString(),
    };
    setLocalEntries((prev) => [...prev, newEntry]);
    setEditingId(newEntry.id);
  }

  function removeEntry(id: string) {
    const entry = localEntries.find((e) => e.id === id);
    if (!entry) return;

    // Draft entry (never saved) — just remove locally
    if (!entry.canonicalTerm.trim()) {
      setLocalEntries((prev) => prev.filter((e) => e.id !== id));
      if (editingId === id) setEditingId(null);
      return;
    }

    const label = `Remove "${entry.canonicalTerm}" from glossary?`;
    if (!confirm(label)) return;
    const updated = localEntries.filter((e) => e.id !== id);
    setLocalEntries(updated);
    if (editingId === id) setEditingId(null);
    updateMutation.mutate({ projectId, glossary: updated });
  }

  function saveEntry(id: string, changes: Partial<GlossaryEntry>) {
    const updated = localEntries.map((e) =>
      e.id === id ? { ...e, ...changes } : e,
    );
    const entry = updated.find((e) => e.id === id);

    // Don't save to server if canonical term is still empty
    if (!entry?.canonicalTerm.trim()) {
      setLocalEntries(updated);
      return;
    }

    setLocalEntries(updated);
    updateMutation.mutate({ projectId, glossary: updated });
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          <BookA className="h-4 w-4" />
          Glossary
          {localEntries.length > 0 && (
            <Badge variant="secondary">{localEntries.length}</Badge>
          )}
        </CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={addEntry}
          disabled={updateMutation.isPending || editingId !== null}
        >
          {updateMutation.isPending ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-1 h-4 w-4" />
          )}
          Add term
        </Button>
      </CardHeader>
      <CardContent>
        {localEntries.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No glossary entries yet. Run an analysis or add terms manually.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Canonical term</TableHead>
                <TableHead>Translation</TableHead>
                <TableHead>Definition</TableHead>
                <TableHead>NOT (rejected)</TableHead>
                <TableHead className="w-[100px]">Source</TableHead>
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {localEntries.map((entry) => (
                <GlossaryRow
                  key={entry.id}
                  entry={entry}
                  isEditing={editingId === entry.id}
                  isSaving={updateMutation.isPending}
                  onStartEdit={() => setEditingId(entry.id)}
                  onSave={(changes) => saveEntry(entry.id, changes)}
                  onRemove={() => removeEntry(entry.id)}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function GlossaryRow({
  entry,
  isEditing,
  isSaving,
  onStartEdit,
  onSave,
  onRemove,
}: {
  entry: GlossaryEntry;
  isEditing: boolean;
  isSaving: boolean;
  onStartEdit: () => void;
  onSave: (changes: Partial<GlossaryEntry>) => void;
  onRemove: () => void;
}) {
  const [editValues, setEditValues] = useState({
    canonicalTerm: entry.canonicalTerm,
    translation: entry.translation ?? "",
    definition: entry.definition ?? "",
    variantsText: entry.variants.join(", "),
  });

  if (isEditing) {
    return (
      <TableRow>
        <TableCell>
          <Label className="sr-only" htmlFor={`term-${entry.id}`}>
            Canonical term
          </Label>
          <Input
            id={`term-${entry.id}`}
            value={editValues.canonicalTerm}
            onChange={(e) =>
              setEditValues((v) => ({ ...v, canonicalTerm: e.target.value }))
            }
            onBlur={() =>
              onSave({
                canonicalTerm: editValues.canonicalTerm,
                translation: editValues.translation || undefined,
                definition: editValues.definition || undefined,
                variants: editValues.variantsText
                  .split(",")
                  .map((v) => v.trim())
                  .filter(Boolean),
              })
            }
            placeholder="Term"
            className="h-8"
          />
        </TableCell>
        <TableCell>
          <Label className="sr-only" htmlFor={`translation-${entry.id}`}>
            Translation
          </Label>
          <Input
            id={`translation-${entry.id}`}
            value={editValues.translation}
            onChange={(e) =>
              setEditValues((v) => ({ ...v, translation: e.target.value }))
            }
            onBlur={() =>
              onSave({ translation: editValues.translation || undefined })
            }
            placeholder="Translation"
            className="h-8"
          />
        </TableCell>
        <TableCell>
          <Label className="sr-only" htmlFor={`definition-${entry.id}`}>
            Definition
          </Label>
          <Input
            id={`definition-${entry.id}`}
            value={editValues.definition}
            onChange={(e) =>
              setEditValues((v) => ({ ...v, definition: e.target.value }))
            }
            onBlur={() =>
              onSave({ definition: editValues.definition || undefined })
            }
            placeholder="Definition"
            className="h-8"
          />
        </TableCell>
        <TableCell>
          <Label className="sr-only" htmlFor={`variants-${entry.id}`}>
            Rejected variants
          </Label>
          <Input
            id={`variants-${entry.id}`}
            value={editValues.variantsText}
            onChange={(e) =>
              setEditValues((v) => ({ ...v, variantsText: e.target.value }))
            }
            onBlur={() =>
              onSave({
                variants: editValues.variantsText
                  .split(",")
                  .map((v) => v.trim())
                  .filter(Boolean),
              })
            }
            placeholder="variant1, variant2"
            className="h-8"
          />
        </TableCell>
        <TableCell>
          <Badge variant="outline" className="text-xs">
            {entry.addedBy}
          </Badge>
        </TableCell>
        <TableCell>
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            disabled={isSaving}
            aria-label={`Remove ${entry.canonicalTerm}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow
      className="cursor-pointer"
      onClick={onStartEdit}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onStartEdit();
        }
      }}
    >
      <TableCell className="font-medium">{entry.canonicalTerm}</TableCell>
      <TableCell className="text-muted-foreground">
        {entry.translation ?? "-"}
      </TableCell>
      <TableCell className="max-w-[300px] truncate text-muted-foreground">
        {entry.definition ?? "-"}
      </TableCell>
      <TableCell>
        {entry.variants.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {entry.variants.map((v) => (
              <Badge key={v} variant="destructive" className="text-xs">
                {v}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="text-xs">
          {entry.addedBy}
        </Badge>
      </TableCell>
      <TableCell>
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          disabled={isSaving}
          aria-label={`Remove ${entry.canonicalTerm}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}
